// @ts-strict-ignore
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { RootState } from '@/reducers/reducers';
import {
    selectDestinationInfoByDestinationStepConfigId,
    selectAppStoreIntegration,
    selectIsAppsDemoModeEnabled,
} from '@/selectors/selectors';
import {
    selectAvailableDestinations,
    selectAvailableVersions,
    createSummaryRequestId,
    selectSummaryViewByRequestId,
    selectSummaryViewEntry,
    selectLatestSummaryViewEntry,
    selectTabularViewDestinationLimit,
    selectActiveVersion,
    selectLiveVersion,
    selectDestinationsData,
} from '@/selectors/summaryViewSelectors';
import { Destination } from '@/types/destinations';
import {
    PageViewType,
    TabularLayoutType,
    ARCHETYPE_PREFIX,
    STATISTIC_PREFIX,
    SummaryViewTableRow,
    OrgSummaryPreferences,
    OrgSummaryPreferencesSnapshot,
    FetchSummaryViewParams,
    UseOrgSummaryParams,
    SummaryViewVersionFilterConfig,
    UseSummaryPreferencesProps,
    FilterOption,
    SUPPORTED_STATISTIC_TYPES,
    DragOrderPreferences,
    SummaryViewResponseAPI,
} from '@/types/summaryView';
import { useGetEntity, useIsLoading } from '@/utils/hooks/helpers';
import { FetchableEntityType } from '@/entityType';
import { createSummaryViewScopeKey } from '@/actions/orgSummaryView';
import {
    getAppSummaryPreferences,
    setAppSummaryPreferences,
    getOrgSummaryPreferences,
    setOrgSummaryPreferences,
} from '@/helpers/localStorageHelper';
import {
    getAvailableMetricKeys,
    getAvailableMetricOptions,
    filterToAvailable,
    applyKeyOrder,
    dedupeKeywordTags,
    mergePreferences,
    normalizePageView,
    normalizeTabularLayout,
    parseListParam,
    buildUrlSearchParams,
    createStandardViewColumns,
    createCompactViewConfig,
    transformToTableRow,
} from '@/helpers/summaryViewHelpers';
import { compareVersions } from '@/helpers';
import { setOrgSummaryAppPreferences, setOrgSummaryOrgPreferences } from '@/reducers/summaryViewPreferences';
import {
    createSummaryViewMetrics,
    type SummaryViewMetricDiscovery,
} from '@/components/orgSummary/overviewTableMetrics';

const SUMMARY_TABLE_PAGE_SIZE = 100;

// How long a cached result stands in for a fresh fetch of the same query. Checked whenever the
// query changes, which is what the cache exists for; sitting on one query does not re-check, so this
// bounds staleness across navigation rather than across idle time.
const SUMMARY_VIEW_CACHE_TTL_MS = 2 * 60 * 1000;

const isSummaryViewEntryFresh = (entry?: { fetchedAt: number }): boolean =>
    !!entry && Date.now() - entry.fetchedAt <= SUMMARY_VIEW_CACHE_TTL_MS;

const getPageFromSearch = (search: string): number => {
    const pageParam = new URLSearchParams(search).get('page');
    if (!pageParam) return 1;

    const parsedPage = Number.parseInt(pageParam, 10);
    if (!Number.isFinite(parsedPage) || parsedPage < 1) {
        return 1;
    }

    return parsedPage;
};

const buildSummaryViewCoverageKey = (destinationId: string, version: string): string => `${version}::${destinationId}`;

const parseSummaryViewScopeKey = (
    scopeKey?: string,
): {
    requestId?: string;
    destinationIds?: string[];
    versions?: string[];
    keywords?: string[];
    offset?: number;
    limit?: number;
} | null => {
    if (!scopeKey) return null;

    try {
        return JSON.parse(scopeKey);
    } catch {
        return null;
    }
};

/**
 * Hook for managing preferences and URL synchronization
 */
const useSummaryPreferences = (props: UseSummaryPreferencesProps) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const demoData = useSelector((state: RootState) => state?.ui?.uiConfig?.demoData);

    const getStoredPreferences = React.useCallback(() => {
        if (props.appId && demoData?.summaryPreferences?.[props.appId]) {
            return demoData.summaryPreferences[props.appId];
        }

        if (props.orgId && demoData?.summaryPreferences) {
            return Object.values(demoData.summaryPreferences)[0]; // Just uses the first app's preferences
        }

        return props.appId ? getAppSummaryPreferences(props.appId) : getOrgSummaryPreferences();
    }, [props.appId]);

    const setStoredPreferences = React.useCallback(
        (preferences: {
            pageView: PageViewType;
            tabularLayout: TabularLayoutType;
            selectedMetrics: string[];
            selectedVersions: string[];
            showLive?: boolean;
            showNext?: boolean;
            selectedDestinationIds: string[];
            dragOrderPreferences?: DragOrderPreferences;
        }) => {
            if (props.appId) {
                return setAppSummaryPreferences(props.appId, preferences);
            }
            return setOrgSummaryPreferences(preferences);
        },
        [props.appId],
    );

    const [pageView, setPageViewState] = React.useState<PageViewType>(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const stored = getStoredPreferences();
        const pageViewParam = urlParams.get('pageView');
        if (pageViewParam) {
            return normalizePageView(pageViewParam);
        }
        return normalizePageView(stored.pageView);
    });

    const [tabularLayout, setTabularLayoutState] = React.useState<TabularLayoutType>(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const stored = getStoredPreferences();
        const tabularLayoutParam = urlParams.get('tabularLayout');
        if (tabularLayoutParam) {
            return normalizeTabularLayout(tabularLayoutParam);
        }
        return normalizeTabularLayout(stored.tabularLayout);
    });

    const [selectedMetricKeys, setSelectedMetricKeysState] = React.useState<string[]>(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const stored = getStoredPreferences();
        const fromUrl = parseListParam(urlParams.get('metrics'));
        const fromStorage = stored.selectedMetrics;
        const explicitPreference = fromUrl ?? fromStorage;

        if (explicitPreference && explicitPreference.length > 0) {
            return filterToAvailable(explicitPreference, props.availableMetrics);
        }

        return props.availableMetrics;
    });

    const [selectedVersions, setSelectedVersionsState] = React.useState<string[]>(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const stored = getStoredPreferences();
        const fromUrl = parseListParam(urlParams.get('versions'));
        const fromStorage = stored.selectedVersions;
        const explicitPreference = fromUrl ?? fromStorage;
        if (explicitPreference && explicitPreference.length > 0) {
            return filterToAvailable(explicitPreference, props.availableVersions);
        }
        return [];
    });

    const [selectedKeywords, setSelectedKeywordsState] = React.useState<string[]>([]);

    const initializeFlagState = (flagName: 'showLive' | 'showNext'): boolean => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('showLive') || urlParams.has('showNext')) {
            return urlParams.get(flagName) === 'true';
        }
        return getStoredPreferences()[flagName] ?? false;
    };

    const [showLive, setShowLiveState] = React.useState<boolean>(() => initializeFlagState('showLive'));
    const [showNext, setShowNextState] = React.useState<boolean>(() => initializeFlagState('showNext'));

    const [selectedDestinationIds, setSelectedDestinationIdsState] = React.useState<string[]>(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const stored = getStoredPreferences();
        const fromUrl = parseListParam(urlParams.get('destinations'));
        const fromStorage = stored.selectedDestinationIds;

        const preferred = fromUrl ?? fromStorage;
        if (!preferred || preferred.length === 0) return [];

        if (props.availableDestinationIds.length === 0) return preferred;
        const filtered = preferred.filter((id) => props.availableDestinationIds.includes(id));
        return filtered.length > 0 ? filtered : [];
    });

    const [dragOrderPreferences, setDragOrderPreferencesState] = React.useState<DragOrderPreferences | undefined>(
        () => {
            const stored = getStoredPreferences();
            return stored.dragOrderPreferences;
        },
    );

    const hasInitializedRef = React.useRef(false);
    const hasInitialUrlSyncRef = React.useRef(false);

    React.useEffect(() => {
        if (hasInitialUrlSyncRef.current) return;
        if (props.availableVersions.length === 0 && props.availableDestinationIds.length === 0) return;
        if (selectedVersions.length === 0) return;
        hasInitialUrlSyncRef.current = true;

        const urlParams = new URLSearchParams(location.search);
        const hasUrlParams =
            urlParams.has('pageView') ||
            urlParams.has('tabularLayout') ||
            urlParams.has('metrics') ||
            urlParams.has('versions') ||
            urlParams.has('destinations');

        if (!hasUrlParams) {
            const currentPrefs = {
                pageView,
                tabularLayout,
                selectedMetrics: selectedMetricKeys,
                selectedVersions,
                selectedDestinationIds,
            };
            const params = buildUrlSearchParams(currentPrefs, location);
            if (params.toString()) {
                history.replace({ ...location, search: params.toString() });
            }
        }
    }, [props.availableVersions, props.availableDestinationIds, selectedVersions]);

    const initializeWithDefaults = React.useCallback(
        (defaults: { versions: string[]; destinationIds: string[] }) => {
            if (hasInitializedRef.current) return;
            hasInitializedRef.current = true;

            const finalVersions = selectedVersions.length > 0 ? selectedVersions : defaults.versions;
            const finalDestinations =
                selectedDestinationIds.length > 0 ? selectedDestinationIds : defaults.destinationIds;

            const needsVersionSync = selectedVersions.length === 0 && finalVersions.length > 0;
            const needsDestinationSync = selectedDestinationIds.length === 0 && finalDestinations.length > 0;

            if (needsVersionSync) {
                setSelectedVersionsState(finalVersions);
            }
            if (needsDestinationSync) {
                setSelectedDestinationIdsState(finalDestinations);
            }

            if (needsVersionSync || needsDestinationSync) {
                const updatedPrefs = {
                    pageView,
                    tabularLayout,
                    selectedMetrics: selectedMetricKeys,
                    selectedVersions: needsVersionSync ? finalVersions : selectedVersions,
                    selectedDestinationIds: needsDestinationSync ? finalDestinations : selectedDestinationIds,
                };
                setStoredPreferences(updatedPrefs);
                const params = buildUrlSearchParams(updatedPrefs, location);
                history.replace({ ...location, search: params.toString() });
            }
        },
        [
            selectedVersions,
            selectedDestinationIds,
            pageView,
            tabularLayout,
            selectedMetricKeys,
            setStoredPreferences,
            history,
            location,
        ],
    );

    const persistToStore = React.useCallback(
        (prefs: OrgSummaryPreferences) => {
            if (props.appId) {
                dispatch(setOrgSummaryAppPreferences(props.appId, prefs));
            } else {
                dispatch(setOrgSummaryOrgPreferences(props.orgId, prefs));
            }
        },
        [dispatch, props.appId, props.orgId],
    );

    const updatePreferences = React.useCallback(
        (changes: {
            pageView?: PageViewType;
            tabularLayout?: TabularLayoutType;
            metrics?: string[];
            versions?: string[];
            keywords?: string[];
            showLive?: boolean;
            showNext?: boolean;
            destinations?: string[];
            dragOrderPreferences?: DragOrderPreferences;
        }) => {
            let nextPageView = pageView;
            let nextTabularLayout = tabularLayout;

            if (changes.pageView !== undefined) {
                nextPageView = changes.pageView;
                setPageViewState(nextPageView);
            }
            if (changes.tabularLayout !== undefined) {
                nextTabularLayout = changes.tabularLayout;
                setTabularLayoutState(nextTabularLayout);
            }
            if (changes.showLive !== undefined) {
                setShowLiveState(changes.showLive);
            }
            if (changes.showNext !== undefined) {
                setShowNextState(changes.showNext);
            }

            const nextDragOrder =
                changes.dragOrderPreferences !== undefined ?
                    {
                        ...dragOrderPreferences,
                        ...changes.dragOrderPreferences,
                        standard: { ...dragOrderPreferences?.standard, ...changes.dragOrderPreferences?.standard },
                        compact: { ...dragOrderPreferences?.compact, ...changes.dragOrderPreferences?.compact },
                    }
                :   dragOrderPreferences;

            if (changes.dragOrderPreferences !== undefined) {
                setDragOrderPreferencesState(nextDragOrder);
            }

            const current = {
                pageView: nextPageView,
                tabularLayout: nextTabularLayout,
                selectedMetrics: selectedMetricKeys,
                selectedVersions,
                showLive: changes.showLive !== undefined ? changes.showLive : showLive,
                showNext: changes.showNext !== undefined ? changes.showNext : showNext,
                selectedDestinationIds,
                dragOrderPreferences: nextDragOrder,
            };

            const available = {
                metrics: props.availableMetrics,
                versions: props.availableVersions,
                destinationIds: props.availableDestinationIds,
            };

            const updatedPrefs = mergePreferences(changes, current, available);

            if (changes.metrics !== undefined) setSelectedMetricKeysState(updatedPrefs.selectedMetrics);
            if (changes.versions !== undefined) setSelectedVersionsState(updatedPrefs.selectedVersions);
            if (changes.keywords !== undefined) setSelectedKeywordsState(dedupeKeywordTags(changes.keywords));
            if (changes.destinations !== undefined) setSelectedDestinationIdsState(updatedPrefs.selectedDestinationIds);

            setStoredPreferences(updatedPrefs);
            persistToStore(updatedPrefs);

            const params = buildUrlSearchParams(updatedPrefs, location);
            const shouldResetPage =
                changes.metrics !== undefined ||
                changes.versions !== undefined ||
                changes.keywords !== undefined ||
                changes.showLive !== undefined ||
                changes.showNext !== undefined ||
                changes.destinations !== undefined ||
                updatedPrefs.pageView !== PageViewType.Tabular;

            if (shouldResetPage) {
                params.delete('page');
            }
            history.replace({ ...location, search: params.toString() });

            return updatedPrefs;
        },
        [
            pageView,
            tabularLayout,
            selectedMetricKeys,
            selectedVersions,
            showLive,
            showNext,
            dragOrderPreferences,
            selectedDestinationIds,
            props.availableMetrics,
            props.availableVersions,
            props.availableDestinationIds,
            history,
            location,
            setStoredPreferences,
            persistToStore,
        ],
    );

    return {
        pageView,
        tabularLayout,
        selectedMetricKeys,
        selectedVersions,
        selectedKeywords,
        showLive,
        showNext,
        selectedDestinationIds,
        dragOrderPreferences,
        updatePreferences,
        getStoredPreferences,
        initializeWithDefaults,
    };
};

export interface SummaryViewContextValue {
    // Core preferences and handlers
    preferences: OrgSummaryPreferencesSnapshot;
    updatePreferences: ReturnType<typeof useSummaryPreferences>['updatePreferences'];
    initializeWithDefaults: ReturnType<typeof useSummaryPreferences>['initializeWithDefaults'];
    getStoredPreferences: ReturnType<typeof useSummaryPreferences>['getStoredPreferences'];
    handleFilterApply: (metrics: string[], versions: string[]) => void;
    handleDestinationChange: (ids: string[]) => void;
    handleKeywordsChange: (keywords: string[]) => void;
    handlePageViewChange: (pageView: PageViewType) => void;
    handleTabularLayoutChange: (tabularLayout: TabularLayoutType) => void;
    // Available options for filters/selectors
    availableDestinations: Destination[];
    availableMetrics: FilterOption[];
    availableVersions: string[];
    availableDestinationIds: string[];
    selectableVersions: string[];
    selectedDestinationIds: string[];
    displayedVersions: string[];
    activeVersion: string | null;
    liveVersion: string | null;

    // Loading states
    loading: {
        isLoading: boolean;
        isLoadingInitialData: boolean;
    };
    error: {
        shouldDisplayFetchFailed: boolean;
        setShouldDisplayFetchFailed: React.Dispatch<React.SetStateAction<boolean>>;
    };

    // Pagination
    pagination: {
        destinationLimit?: number;
        currentPage: number;
        pageSize: number;
        totalPages: number;
        setCurrentPage: (page: number) => void;
    };

    // Data fetching
    refetchCurrentPage: () => void;
    requestId: string;
    summaryViewResponse: SummaryViewResponseAPI | undefined;
    setStandardRowOrder: (rowOrder: string[] | undefined) => void;

    // Table data
    tableData: {
        standardColumns: ReturnType<typeof createStandardViewColumns>;
        compactConfig: ReturnType<typeof createCompactViewConfig>;
        transformedRows: SummaryViewTableRow[];
        filteredRows: SummaryViewTableRow[];
        metrics: ReturnType<typeof createSummaryViewMetrics>;
    };

    // Count metadata for pagination and display
    selectedTotalCount: number | null;
    availableTotalCount: number | null;
    isLoadingCount: boolean;

    // Integration info (for rendering)
    appStoreIntegration: ReturnType<typeof selectAppStoreIntegration>;
}

/**
 * Main hook for summary view functionality
 */
export const useSummaryView = ({
    orgId,
    appId,
    availableDestinations: propDestinations,
}: UseOrgSummaryParams): SummaryViewContextValue => {
    const history = useHistory();
    const location = useLocation();
    const requestId = createSummaryRequestId(orgId, appId);

    const selectorDestinations = useSelector((state: RootState) => selectAvailableDestinations(state, orgId, appId));
    const selectorVersions = useSelector((state: RootState) => selectAvailableVersions(state, orgId, appId));
    const destinationLimit = useSelector((state: RootState) => selectTabularViewDestinationLimit(state, orgId));
    const isLoadingOrgDestinations = useIsLoading(FetchableEntityType.OrgDestinationsAndVersions, orgId);
    const activeVersion = useSelector((state: RootState) => selectActiveVersion(state, orgId, appId));
    const liveVersion = useSelector((state: RootState) => selectLiveVersion(state, orgId, appId));

    const orgDestinationsData = useSelector((state: RootState) => selectDestinationsData(state, orgId));
    const isViewingAppsInDemoMode = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));
    const demoSummaryViewResponse = useSelector((state: RootState) => selectSummaryViewByRequestId(state, requestId));

    const storeDestinations = useSelector((state: RootState) =>
        appId ? Object.values(selectDestinationInfoByDestinationStepConfigId(state, appId)) : [],
    );

    const resolvedDestinations = React.useMemo(() => {
        if (propDestinations && propDestinations.length > 0) return propDestinations;
        if (selectorDestinations.length > 0) return selectorDestinations;
        return storeDestinations;
    }, [propDestinations, selectorDestinations, storeDestinations]);

    const availableDestinationIds = React.useMemo(
        () => resolvedDestinations.map((dest) => dest.stepConfigId),
        [resolvedDestinations],
    );

    const appStoreIntegration = useSelector((state: RootState) => selectAppStoreIntegration(state, appId));
    const availableVersions = selectorVersions;

    const availableMetricKeys = React.useMemo(() => getAvailableMetricKeys(SUPPORTED_STATISTIC_TYPES), []);

    const {
        pageView,
        tabularLayout,
        selectedMetricKeys,
        selectedVersions,
        selectedKeywords,
        showLive,
        showNext,
        selectedDestinationIds,
        dragOrderPreferences,
        updatePreferences,
        initializeWithDefaults,
        getStoredPreferences,
    } = useSummaryPreferences({
        orgId,
        appId,
        availableMetrics: availableMetricKeys,
        availableVersions,
        availableDestinationIds,
    });

    const preferences: OrgSummaryPreferencesSnapshot = React.useMemo(
        () => ({
            pageView,
            tabularLayout,
            selectedMetricKeys,
            showLive,
            showNext,
            selectedVersions,
            selectedKeywords,
            selectedDestinationIds,
            dragOrderPreferences,
        }),
        [
            pageView,
            tabularLayout,
            selectedMetricKeys,
            selectedVersions,
            selectedKeywords,
            showLive,
            showNext,
            selectedDestinationIds,
            dragOrderPreferences,
        ],
    );

    const displayedVersions = React.useMemo(() => {
        const hasActiveFlags = showLive || showNext;

        if (hasActiveFlags) {
            const versions = new Set<string>();
            if (showLive && liveVersion) {
                versions.add(liveVersion);
            }
            if (showNext && activeVersion) {
                versions.add(activeVersion);
            }
            return Array.from(versions).sort(compareVersions);
        }

        return [...selectedVersions].sort(compareVersions);
    }, [selectedVersions, showLive, showNext, liveVersion, activeVersion]);

    const [shouldDisplayFetchFailed, setShouldDisplayFetchFailed] = React.useState(false);
    const currentPage = React.useMemo(() => getPageFromSearch(location.search), [location.search]);
    const pageSize = SUMMARY_TABLE_PAGE_SIZE;
    const pageOffset = (currentPage - 1) * pageSize;
    const [selectedTotalCount, setSelectedTotalCount] = React.useState<number | null>(null);
    const [availableTotalCount, setAvailableTotalCount] = React.useState<number | null>(null);
    const [isLoadingCount, setIsLoadingCount] = React.useState(false);

    const [requestSummaryView, isLoadingSummaryView, summaryViewError] = useGetEntity(
        FetchableEntityType.OrgSummaryView,
        requestId,
    );
    const lastAutoFetchSignatureRef = React.useRef<string | null>(null);
    const [standardRowOrder, setStandardRowOrder] = React.useState<string[] | undefined>();

    React.useEffect(() => {
        lastAutoFetchSignatureRef.current = null;
        setShouldDisplayFetchFailed(false);
        setStandardRowOrder(undefined);
    }, [requestId]);

    React.useEffect(() => {
        if (summaryViewError) {
            setShouldDisplayFetchFailed(true);
        }
    }, [summaryViewError]);

    const appToDestinationIds = React.useMemo(() => {
        if (appId) {
            return { [appId]: selectedDestinationIds };
        }

        const destinationIdSet = new Set(selectedDestinationIds);
        return (orgDestinationsData?.appDestinations ?? []).reduce(
            (acc, appDestination) => {
                const matchingDestinationIds = (appDestination.destinations ?? []).reduce((ids, destination) => {
                    if (destination.stepConfigId && destinationIdSet.has(destination.stepConfigId)) {
                        ids.push(destination.stepConfigId);
                    }
                    return ids;
                }, [] as string[]);

                if (matchingDestinationIds.length > 0) {
                    acc[appDestination.appId] = matchingDestinationIds;
                }

                return acc;
            },
            {} as Record<string, string[]>,
        );
    }, [appId, selectedDestinationIds, orgDestinationsData?.appDestinations]);

    const liveSummaryViewEntry = useSelector((state: RootState) => selectLatestSummaryViewEntry(state, requestId));
    const currentScopeKey = React.useMemo(
        () =>
            createSummaryViewScopeKey({
                requestId,
                appToDestinationIds,
                keywords: selectedKeywords,
                offset: pageOffset,
                limit: pageSize,
            }),
        [requestId, appToDestinationIds, selectedKeywords, pageOffset],
    );
    const currentRowSetSignature = React.useMemo(
        () =>
            createSummaryViewScopeKey({
                requestId,
                appToDestinationIds,
                versions: displayedVersions,
                keywords: selectedKeywords,
                offset: pageOffset,
                limit: pageSize,
            }),
        [requestId, appToDestinationIds, displayedVersions, selectedKeywords, pageOffset],
    );
    const selectedDestinationIdsSet = React.useMemo(() => new Set(selectedDestinationIds), [selectedDestinationIds]);
    const displayedVersionsSet = React.useMemo(() => new Set(displayedVersions), [displayedVersions]);
    const cachedRowSetEntry = useSelector((state: RootState) =>
        selectSummaryViewEntry(state, requestId, currentRowSetSignature),
    );
    const currentRowSetEntry = React.useMemo(
        () => (isSummaryViewEntryFresh(cachedRowSetEntry) ? cachedRowSetEntry : undefined),
        [cachedRowSetEntry],
    );
    const derivedSinglePageRowSetEntry = React.useMemo(() => {
        if (isViewingAppsInDemoMode || currentRowSetEntry || !liveSummaryViewEntry) {
            return undefined;
        }
        if (!isSummaryViewEntryFresh(liveSummaryViewEntry)) {
            return undefined;
        }
        if (pageView !== PageViewType.Tabular || currentPage !== 1 || pageOffset !== 0) {
            return undefined;
        }

        const parsedScopeKey = parseSummaryViewScopeKey(liveSummaryViewEntry.scopeKey);
        if (
            !parsedScopeKey ||
            (parsedScopeKey.offset ?? 0) !== pageOffset ||
            (parsedScopeKey.limit ?? 0) !== pageSize ||
            (parsedScopeKey.keywords ?? []).join(',') !== selectedKeywords.join(',')
        ) {
            return undefined;
        }

        const cachedItems = liveSummaryViewEntry.summaryView.versionSummaryViewItems ?? [];
        if (cachedItems.length === 0) {
            return undefined;
        }

        const hasSinglePageCoverage =
            cachedItems.length < pageSize || (selectedTotalCount !== null && selectedTotalCount <= pageSize);
        if (!hasSinglePageCoverage) {
            return undefined;
        }

        const cachedDestinationIds = new Set(cachedItems.map((item) => item.destinationId));
        const cachedVersions = new Set(cachedItems.map((item) => item.version));

        if (!selectedDestinationIds.every((destinationId) => cachedDestinationIds.has(destinationId))) {
            return undefined;
        }

        if (!displayedVersions.every((version) => cachedVersions.has(version))) {
            return undefined;
        }

        const filteredItems = cachedItems.filter(
            (item) => selectedDestinationIdsSet.has(item.destinationId) && displayedVersionsSet.has(item.version),
        );

        const filteredCoverageByRowKey = filteredItems.reduce<Record<string, string[]>>((acc, item) => {
            const coverageKey = buildSummaryViewCoverageKey(item.destinationId, item.version);
            acc[coverageKey] = [...(liveSummaryViewEntry.fetchedPropertiesByRowKey?.[coverageKey] ?? [])];
            return acc;
        }, {});

        return {
            ...liveSummaryViewEntry,
            scopeKey: currentScopeKey,
            rowSetSignature: currentRowSetSignature,
            fetchedPropertiesByRowKey: filteredCoverageByRowKey,
            summaryView: {
                ...liveSummaryViewEntry.summaryView,
                versionSummaryViewItems: filteredItems,
            },
        };
    }, [
        isViewingAppsInDemoMode,
        currentRowSetEntry,
        liveSummaryViewEntry,
        pageView,
        currentPage,
        pageOffset,
        selectedTotalCount,
        selectedDestinationIds,
        displayedVersions,
        selectedKeywords,
        selectedDestinationIdsSet,
        displayedVersionsSet,
        currentScopeKey,
        currentRowSetSignature,
    ]);
    const effectiveRowSetEntry = currentRowSetEntry ?? derivedSinglePageRowSetEntry;
    const summaryViewResponse = isViewingAppsInDemoMode ? demoSummaryViewResponse : effectiveRowSetEntry?.summaryView;
    const fetchedPropertiesByCoverageKey = React.useMemo(() => {
        if (!effectiveRowSetEntry) {
            return {};
        }

        if (effectiveRowSetEntry.fetchedPropertiesByRowKey) {
            return effectiveRowSetEntry.fetchedPropertiesByRowKey;
        }

        const fallbackCoverage: Record<string, string[]> = {};
        (effectiveRowSetEntry.summaryView.versionSummaryViewItems ?? []).forEach((item) => {
            fallbackCoverage[buildSummaryViewCoverageKey(item.destinationId, item.version)] = [
                ...(effectiveRowSetEntry.fetchedProperties ?? []),
            ];
        });
        return fallbackCoverage;
    }, [effectiveRowSetEntry]);

    const fetchSummaryViewRequest = React.useCallback(
        (request: FetchSummaryViewParams | null) => {
            if (isViewingAppsInDemoMode || !request) {
                return;
            }

            setShouldDisplayFetchFailed(false);
            requestSummaryView({
                orgId,
                appToDestinationIds,
                versions: request.versions,
                properties: request.properties,
                keywords: selectedKeywords,
                offset: request.offset,
                limit: request.limit,
            });
        },
        [isViewingAppsInDemoMode, orgId, appToDestinationIds, selectedKeywords, requestSummaryView],
    );

    const handleSetShouldDisplayFetchFailed = React.useCallback((value: boolean | ((prev: boolean) => boolean)) => {
        setShouldDisplayFetchFailed(value);
    }, []);

    const missingMetricKeys = React.useMemo(() => {
        if (pageView !== PageViewType.Tabular) {
            return [];
        }
        if (displayedVersions.length === 0 || selectedDestinationIds.length === 0) {
            return [];
        }
        if (!effectiveRowSetEntry || selectedMetricKeys.length === 0) {
            return [];
        }

        const coverageKeys = effectiveRowSetEntry.summaryView.versionSummaryViewItems.map((item) =>
            buildSummaryViewCoverageKey(item.destinationId, item.version),
        );

        if (coverageKeys.length === 0) {
            return [];
        }

        return selectedMetricKeys.filter((metricKey) =>
            coverageKeys.some(
                (coverageKey) => !(fetchedPropertiesByCoverageKey[coverageKey] ?? []).includes(metricKey),
            ),
        );
    }, [
        pageView,
        displayedVersions,
        selectedDestinationIds,
        effectiveRowSetEntry,
        selectedMetricKeys,
        fetchedPropertiesByCoverageKey,
    ]);
    const pendingSummaryRequest = React.useMemo<FetchSummaryViewParams | null>(() => {
        if (pageView !== PageViewType.Tabular) {
            return null;
        }
        if (displayedVersions.length === 0 || selectedDestinationIds.length === 0 || selectedMetricKeys.length === 0) {
            return null;
        }

        if (!effectiveRowSetEntry) {
            return {
                destinationIds: selectedDestinationIds,
                versions: displayedVersions,
                properties: selectedMetricKeys,
                offset: pageOffset,
                limit: pageSize,
            };
        }

        if (missingMetricKeys.length === 0) {
            return null;
        }

        return {
            destinationIds: selectedDestinationIds,
            versions: displayedVersions,
            properties: missingMetricKeys,
            offset: pageOffset,
            limit: pageSize,
        };
    }, [
        pageView,
        displayedVersions,
        selectedDestinationIds,
        selectedMetricKeys,
        effectiveRowSetEntry,
        missingMetricKeys,
        pageOffset,
    ]);
    const pendingSummaryRequestSignature = React.useMemo(() => {
        if (!pendingSummaryRequest) {
            return '';
        }

        return JSON.stringify({
            scopeKey: currentScopeKey,
            rowSetSignature: currentRowSetSignature,
            request: {
                destinationIds: pendingSummaryRequest.destinationIds,
                versions: pendingSummaryRequest.versions,
                properties: pendingSummaryRequest.properties,
                offset: pendingSummaryRequest.offset ?? 0,
                limit: pendingSummaryRequest.limit ?? 0,
            },
        });
    }, [currentScopeKey, currentRowSetSignature, pendingSummaryRequest]);

    const refetchCurrentPage = React.useCallback(() => {
        if (isLoadingSummaryView) {
            return;
        }
        if (!pendingSummaryRequest) {
            return;
        }

        lastAutoFetchSignatureRef.current = pendingSummaryRequestSignature || null;
        fetchSummaryViewRequest(pendingSummaryRequest);
    }, [isLoadingSummaryView, pendingSummaryRequest, pendingSummaryRequestSignature, fetchSummaryViewRequest]);

    // Dispatched even while a request is in flight: the saga takes the latest, so the newest query
    // supersedes the old one instead of queueing behind it.
    React.useEffect(() => {
        if (isViewingAppsInDemoMode) return;
        if (!pendingSummaryRequest) {
            lastAutoFetchSignatureRef.current = null;
            return;
        }
        if (pendingSummaryRequestSignature === lastAutoFetchSignatureRef.current) return;

        lastAutoFetchSignatureRef.current = pendingSummaryRequestSignature;
        fetchSummaryViewRequest(pendingSummaryRequest);
    }, [isViewingAppsInDemoMode, pendingSummaryRequest, pendingSummaryRequestSignature, fetchSummaryViewRequest]);

    const metricOptions = React.useMemo(() => getAvailableMetricOptions(SUPPORTED_STATISTIC_TYPES), []);

    const transformedRowCacheRef = React.useRef(
        new Map<
            string,
            {
                sourceItem: NonNullable<typeof summaryViewResponse>['versionSummaryViewItems'][number];
                row: SummaryViewTableRow;
            }
        >(),
    );

    const transformedRows = React.useMemo(() => {
        const items = summaryViewResponse?.versionSummaryViewItems ?? [];
        const cache = transformedRowCacheRef.current;

        if (items.length === 0) {
            cache.clear();
            return [];
        }

        const nextRows: SummaryViewTableRow[] = [];
        const seenKeys = new Set<string>();

        items.forEach((item) => {
            const cacheKey = `${item.version}::${item.destinationId}`;
            seenKeys.add(cacheKey);

            const cached = cache.get(cacheKey);
            if (cached?.sourceItem === item) {
                nextRows.push(cached.row);
                return;
            }

            const row = transformToTableRow(item);
            cache.set(cacheKey, { sourceItem: item, row });
            nextRows.push(row);
        });

        Array.from(cache.keys()).forEach((cacheKey) => {
            if (!seenKeys.has(cacheKey)) {
                cache.delete(cacheKey);
            }
        });

        return nextRows;
    }, [summaryViewResponse?.versionSummaryViewItems]);

    const selectableVersions = React.useMemo(() => {
        const merged = new Set<string>();
        availableVersions.forEach((version) => merged.add(version));
        selectedVersions.forEach((version) => merged.add(version));

        const fetchedVersions = new Set(transformedRows.map((row) => row.version));

        if (showLive && liveVersion && fetchedVersions.has(liveVersion)) {
            merged.add(liveVersion);
        }
        if (showNext && activeVersion && fetchedVersions.has(activeVersion)) {
            merged.add(activeVersion);
        }

        return Array.from(merged).sort(compareVersions);
    }, [availableVersions, selectedVersions, showLive, showNext, liveVersion, activeVersion, transformedRows]);

    const matchingRows = React.useMemo(() => {
        return transformedRows.filter(
            (row) => displayedVersionsSet.has(row.version) && selectedDestinationIdsSet.has(row.destinationId),
        );
    }, [transformedRows, displayedVersionsSet, selectedDestinationIdsSet]);

    const filteredRows = React.useMemo(() => {
        if (!isViewingAppsInDemoMode) {
            return matchingRows;
        }

        return matchingRows.slice(pageOffset, pageOffset + pageSize);
    }, [matchingRows, isViewingAppsInDemoMode, pageOffset, pageSize]);

    const rowMetricDiscoveryCacheRef = React.useRef(
        new Map<
            string,
            {
                row: SummaryViewTableRow;
                multiInstanceKeysByProperty: Record<string, string[]>;
                sampleStatisticsByProperty: Record<string, any>;
            }
        >(),
    );

    const metricDiscovery = React.useMemo<SummaryViewMetricDiscovery>(() => {
        const cache = rowMetricDiscoveryCacheRef.current;
        const multiInstanceKeysByProperty = new Map<string, Set<string>>();
        const sampleStatisticsByProperty: Record<string, any> = {};

        filteredRows.forEach((row) => {
            const cached = cache.get(row.key);

            const rowDiscovery =
                cached?.row === row ?
                    cached
                :   (() => {
                        const nextMultiInstanceKeysByProperty: Record<string, string[]> = {};
                        const nextSampleStatisticsByProperty: Record<string, any> = {};

                        Object.keys(row).forEach((key) => {
                            if (key.startsWith(STATISTIC_PREFIX) && row[key] !== undefined) {
                                nextSampleStatisticsByProperty[key] = row[key];
                            }

                            if (!key.startsWith(`${ARCHETYPE_PREFIX}:`)) return;

                            const firstColonIndex = key.indexOf(':');
                            const lastColonIndex = key.lastIndexOf(':');
                            if (lastColonIndex <= firstColonIndex) return;

                            const propertyKey = key.slice(0, lastColonIndex);
                            nextMultiInstanceKeysByProperty[propertyKey] = [
                                ...(nextMultiInstanceKeysByProperty[propertyKey] ?? []),
                                key,
                            ];
                        });

                        const nextDiscovery = {
                            row,
                            multiInstanceKeysByProperty: nextMultiInstanceKeysByProperty,
                            sampleStatisticsByProperty: nextSampleStatisticsByProperty,
                        };
                        cache.set(row.key, nextDiscovery);
                        return nextDiscovery;
                    })();

            selectedMetricKeys.forEach((propertyKey) => {
                rowDiscovery.multiInstanceKeysByProperty[propertyKey]?.forEach((instanceKey) => {
                    if (!multiInstanceKeysByProperty.has(propertyKey)) {
                        multiInstanceKeysByProperty.set(propertyKey, new Set());
                    }
                    multiInstanceKeysByProperty.get(propertyKey)!.add(instanceKey);
                });

                if (
                    sampleStatisticsByProperty[propertyKey] === undefined &&
                    rowDiscovery.sampleStatisticsByProperty[propertyKey] !== undefined
                ) {
                    sampleStatisticsByProperty[propertyKey] = rowDiscovery.sampleStatisticsByProperty[propertyKey];
                }
            });
        });

        const multiInstanceKeysByPropertyRecord: Record<string, string[]> = {};
        multiInstanceKeysByProperty.forEach((instanceKeys, propertyKey) => {
            multiInstanceKeysByPropertyRecord[propertyKey] = Array.from(instanceKeys);
        });

        return {
            multiInstanceKeysByProperty: multiInstanceKeysByPropertyRecord,
            sampleStatisticsByProperty,
        };
    }, [filteredRows, selectedMetricKeys]);

    const orderedFilteredRows = React.useMemo(
        () => applyKeyOrder(filteredRows, standardRowOrder),
        [filteredRows, standardRowOrder],
    );

    React.useEffect(() => {
        setStandardRowOrder(undefined);
    }, [currentPage, selectedMetricKeys, selectedDestinationIds, displayedVersions]);

    const metrics = React.useMemo(() => {
        return createSummaryViewMetrics(selectedMetricKeys, metricDiscovery);
    }, [selectedMetricKeys, metricDiscovery]);

    const destinationsById = React.useMemo(() => {
        const map: Record<string, Destination> = {};
        resolvedDestinations.forEach((dest) => {
            map[dest.stepConfigId] = dest;
        });
        return map;
    }, [resolvedDestinations]);

    const handleFilterApply = React.useCallback(
        (newMetrics: string[], newVersions: string[]) => {
            const hasActiveFlags = showLive || showNext;
            let nextShowLive = showLive;
            let nextShowNext = showNext;
            let explicitVersions = newVersions;

            if (hasActiveFlags) {
                // Case 1: Check if resolved versions were unchecked
                if (showLive && liveVersion && !newVersions.includes(liveVersion)) {
                    nextShowLive = false;
                }
                if (showNext && activeVersion && !newVersions.includes(activeVersion)) {
                    nextShowNext = false;
                }

                // Case 2: Check if user selected a non-resolved version
                const hasNonResolvedSelections = newVersions.some((v) => v !== liveVersion && v !== activeVersion);

                if (hasNonResolvedSelections) {
                    // Turn off all flags and switch to explicit mode
                    nextShowLive = false;
                    nextShowNext = false;
                    // Keep all selections (including previously resolved ones)
                    explicitVersions = newVersions;
                } else {
                    // Only resolved versions selected, filter them out from explicit
                    explicitVersions = newVersions.filter((v) => {
                        if (showLive && v === liveVersion) return false;
                        if (showNext && v === activeVersion) return false;
                        return true;
                    });
                }
            }

            const updates: {
                metrics: string[];
                versions: string[];
                showLive?: boolean;
                showNext?: boolean;
            } = {
                metrics: newMetrics,
                versions: explicitVersions,
            };

            if (nextShowLive !== showLive) {
                updates.showLive = nextShowLive;
            }
            if (nextShowNext !== showNext) {
                updates.showNext = nextShowNext;
            }

            setStandardRowOrder(undefined);
            setShouldDisplayFetchFailed(false);
            updatePreferences(updates);
        },
        [updatePreferences, showLive, showNext, activeVersion, liveVersion],
    );

    const tabularVersionFilter: SummaryViewVersionFilterConfig | undefined = React.useMemo(() => {
        if (pageView !== PageViewType.Tabular) {
            return undefined;
        }
        return {
            availableVersions: selectableVersions,
            displayedVersions,
            onApply: (versions: string[]) => {
                handleFilterApply(selectedMetricKeys, versions);
            },
            isSubmitting: isLoadingSummaryView || isLoadingOrgDestinations,
        };
    }, [
        pageView,
        selectableVersions,
        displayedVersions,
        handleFilterApply,
        selectedMetricKeys,
        isLoadingSummaryView,
        isLoadingOrgDestinations,
    ]);

    const standardColumns = React.useMemo(
        () =>
            createStandardViewColumns({
                metrics,
                currentAppId: appId,
                orgId,
                destinationsById,
                appStoreIntegration,
                metricColumnOrder: dragOrderPreferences?.standard?.metricColumnOrder,
                versionFilter: tabularVersionFilter,
            }),
        [
            metrics,
            appId,
            orgId,
            destinationsById,
            appStoreIntegration,
            dragOrderPreferences?.standard?.metricColumnOrder,
            tabularVersionFilter,
        ],
    );

    const compactViewConfig = React.useMemo(
        () =>
            createCompactViewConfig({
                releasesData: filteredRows,
                metrics,
                currentAppId: appId,
                orgId,
                destinationsById,
                destinationColumnOrder: dragOrderPreferences?.compact?.destinationColumnOrder,
                metricRowOrder: dragOrderPreferences?.compact?.metricRowOrder,
                versionFilter: tabularVersionFilter,
            }),
        [
            filteredRows,
            metrics,
            appId,
            orgId,
            destinationsById,
            dragOrderPreferences?.compact?.destinationColumnOrder,
            dragOrderPreferences?.compact?.metricRowOrder,
            tabularVersionFilter,
        ],
    );

    const handleDestinationChange = React.useCallback(
        (ids: string[]) => {
            setShouldDisplayFetchFailed(false);
            setStandardRowOrder(undefined);
            updatePreferences({ destinations: ids });
        },
        [updatePreferences],
    );

    const handleKeywordsChange = React.useCallback(
        (keywords: string[]) => {
            setShouldDisplayFetchFailed(false);
            setStandardRowOrder(undefined);
            updatePreferences({ keywords });
        },
        [updatePreferences],
    );

    const handlePageViewChange = React.useCallback(
        (value: PageViewType) => {
            updatePreferences({ pageView: value });
        },
        [updatePreferences],
    );

    const handleTabularLayoutChange = React.useCallback(
        (value: TabularLayoutType) => {
            updatePreferences({ tabularLayout: value });
        },
        [updatePreferences],
    );

    React.useEffect(() => {
        if (
            pageView !== PageViewType.Tabular ||
            displayedVersions.length === 0 ||
            selectedDestinationIds.length === 0
        ) {
            setSelectedTotalCount(null);
            setAvailableTotalCount(null);
            setIsLoadingCount(false);
            return;
        }

        if (isViewingAppsInDemoMode) {
            setSelectedTotalCount(matchingRows.length);
            setAvailableTotalCount(transformedRows.filter((row) => displayedVersionsSet.has(row.version)).length);
            setIsLoadingCount(false);
            return;
        }

        if (summaryViewResponse !== undefined) {
            setSelectedTotalCount(summaryViewResponse.selectedTotalCount ?? null);
            setAvailableTotalCount(summaryViewResponse.availableTotalCount ?? null);
            setIsLoadingCount(false);
        } else {
            setIsLoadingCount(isLoadingSummaryView);
        }
    }, [
        pageView,
        displayedVersions,
        selectedDestinationIds,
        isViewingAppsInDemoMode,
        matchingRows.length,
        transformedRows,
        displayedVersionsSet,
        summaryViewResponse,
        isLoadingSummaryView,
    ]);

    const totalPages = React.useMemo(() => {
        if (selectedTotalCount === null || selectedTotalCount <= 0) {
            return 1;
        }

        return Math.max(1, Math.ceil(selectedTotalCount / pageSize));
    }, [selectedTotalCount, pageSize]);

    const setCurrentPage = React.useCallback(
        (page: number) => {
            const nextPage = Math.max(1, Math.floor(page || 1));
            if (nextPage === currentPage) return;

            const params = new URLSearchParams(location.search);
            if (nextPage === 1) {
                params.delete('page');
            } else {
                params.set('page', String(nextPage));
            }

            setStandardRowOrder(undefined);
            setShouldDisplayFetchFailed(false);
            history.replace({ ...location, search: params.toString() });
        },
        [currentPage, history, location],
    );

    React.useEffect(() => {
        if (pageView !== PageViewType.Tabular || isLoadingCount || selectedTotalCount === null) return;
        if (currentPage <= totalPages) return;

        setCurrentPage(1);
    }, [pageView, isLoadingCount, selectedTotalCount, currentPage, totalPages, setCurrentPage]);

    const isLoading = isLoadingSummaryView || isLoadingOrgDestinations;

    return {
        preferences,
        updatePreferences,
        initializeWithDefaults,
        getStoredPreferences,
        handleFilterApply,
        handleDestinationChange,
        handleKeywordsChange,
        handlePageViewChange,
        handleTabularLayoutChange,
        availableDestinations: resolvedDestinations,
        availableMetrics: metricOptions,
        availableVersions,
        availableDestinationIds,
        selectableVersions,
        selectedDestinationIds,
        displayedVersions,
        activeVersion,
        liveVersion,

        loading: {
            isLoading,
            isLoadingInitialData: isLoadingOrgDestinations,
        },
        error: {
            shouldDisplayFetchFailed,
            setShouldDisplayFetchFailed: handleSetShouldDisplayFetchFailed,
        },

        pagination: {
            destinationLimit,
            currentPage,
            pageSize,
            totalPages,
            setCurrentPage,
        },

        refetchCurrentPage,
        requestId,
        summaryViewResponse,
        setStandardRowOrder,

        tableData: {
            standardColumns,
            compactConfig: compactViewConfig,
            transformedRows,
            filteredRows: orderedFilteredRows,
            metrics,
        },

        selectedTotalCount,
        availableTotalCount,
        isLoadingCount,

        appStoreIntegration,
    };
};
